import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonSpinner,
} from '@ionic/react';
import { useMutation } from '@apollo/client/react';
import { UPDATE_BID_STATUS } from '../../../graphql/mutations/marketEntry';
import { UPDATE_TRANSFER_OFFER_STATUS } from '../../../graphql/mutations/transferOffer';
import type {
  UpdateBidStatusMutation,
  UpdateBidStatusMutationVariables,
  UpdateTransferOfferStatusMutation,
  UpdateTransferOfferStatusMutationVariables,
} from '../../../types/graphql-generated';
import { PlayerCard } from '../components/PlayerCard';
import { useState } from 'react';
import type { ReviewItem } from './reviewItem';

export type { ReviewItem };
export { ReviewItemFactory } from './reviewItem';

interface BidReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: ReviewItem;
  leagueId: string;
  onSuccess: () => void;
}

export const BidReviewModal = ({
  isOpen,
  onClose,
  item,
  leagueId,
  onSuccess,
}: BidReviewModalProps) => {
  const [updateBidStatus, { loading: bidLoading }] = useMutation<
    UpdateBidStatusMutation,
    UpdateBidStatusMutationVariables
  >(UPDATE_BID_STATUS);

  const [updateTransferOfferStatus, { loading: offerLoading }] = useMutation<
    UpdateTransferOfferStatusMutation,
    UpdateTransferOfferStatusMutationVariables
  >(UPDATE_TRANSFER_OFFER_STATUS);

  const loading = bidLoading || offerLoading;
  const [error, setError] = useState<string | null>(null);

  const player = item.getPlayer();
  const bidderName = item.getBidderName();
  const amount = item.getAmount();

  const handleUpdateStatus = async (newStatus: 'accepted' | 'rejected') => {
    try {
      setError(null);
      const errors = await item.execute(newStatus, leagueId, { updateBidStatus, updateTransferOfferStatus });
      if (errors.length > 0) {
        setError(errors.join(', '));
        return;
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating offer status:', err);
      setError('Error al actualizar la oferta. Por favor, inténtalo de nuevo.');
    }
  };

  const handleAccept = () => handleUpdateStatus('accepted');
  const handleReject = () => handleUpdateStatus('rejected');

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Revisar oferta</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose} disabled={loading}>
              Cerrar
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {/* Player Info */}
        <div className="space-y-2">
          <h3 className="text-lg font-semibold mb-3">Jugador</h3>
          <PlayerCard player={player} />
        </div>

        {/* Offer Info */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Oferta</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Ofertante:</span>
              <span className="font-semibold">{bidderName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Cantidad ofrecida:</span>
              <div className="px-2 py-0.5 rounded bg-blue-100 text-blue-900 font-semibold text-sm">
                {amount.toLocaleString('es-ES')}€
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <IonButton
            expand="block"
            onClick={handleAccept}
            disabled={loading}
            style={{
              '--background': '#257c2c',
              '--background-hover': '#15803d',
              '--background-activated': '#15803d',
              '--color': '#ffffff',
            } as React.CSSProperties}
          >
            {loading ? <IonSpinner name="crescent" /> : 'Aceptar'}
          </IonButton>
          <IonButton
            expand="block"
            style={{
              '--background': '#ca0c0c',
              '--background-hover': '#ca0c0c',
              '--background-activated': '#f30f0f',
              '--color': '#ffffff',
            } as React.CSSProperties}
            onClick={handleReject}
            disabled={loading}
          >
            {loading ? <IonSpinner name="crescent" /> : 'Rechazar'}
          </IonButton>
          <IonButton
            expand="block"
            fill="outline"
            onClick={onClose}
            disabled={loading}
          >
            Volver
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};
